import type React from 'react'
import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  ListSubheader,
  IconButton,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Divider,
  Stack,
  Typography,
  CircularProgress,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { TEST_IDS } from '../../test-ids'
import { placeOrder, editOrder } from '../../store/orders'
import * as orderApi from '../../api/order'
import type { Product } from '../../api/order'

interface OrderRow {
  sku: string
  name: string
  quantity: number
  unitPrice?: number
  currency?: string
}

interface PlaceOrderDialogProps {
  open: boolean
  order?: any | null
  isAdmin?: boolean
  onClose: () => void
  onOrderPlaced?: () => void
  onOrderUpdated?: () => void
}

export function PlaceOrderDialog({
  open,
  order,
  isAdmin,
  onClose,
  onOrderPlaced,
  onOrderUpdated,
}: PlaceOrderDialogProps) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isEditMode = !!order
  const [rows, setRows] = useState<OrderRow[]>([
    { sku: '', name: '', quantity: 1 },
  ])
  const [invoicedAt, setInvoicedAt] = useState<string>('')
  const [shippedAt, setShippedAt] = useState<string>('')
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(false)
  const [productsLoading, setProductsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isLocked = isEditMode && (order?.invoicedAt || order?.shippedAt)

  const formatPrice = (price: number, currency: string) => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    })
    return formatter.format(price)
  }

  const getProductPriceDisplay = (product: Product) => {
    const msrp = formatPrice(product.price, product.currency)

    if (product.discountedPrice !== undefined) {
      const discounted = formatPrice(product.discountedPrice, product.currency)
      return (
        <Stack spacing={0.5}>
          <Typography
            variant="body2"
            sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
          >
            {msrp}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 'bold', color: 'success.main' }}
          >
            {discounted}
          </Typography>
        </Stack>
      )
    }

    return <Typography variant="body2">{msrp}</Typography>
  }

  useEffect(() => {
    if (open) {
      if (isEditMode) {
        const orderDetails = Array.isArray(order.orderDetails)
          ? order.orderDetails
          : order.orderDetails
            ? [order.orderDetails]
            : []
        setRows(orderDetails)
        setInvoicedAt(order.invoicedAt || '')
        setShippedAt(order.shippedAt || '')
      } else {
        setRows([{ sku: '', name: '', quantity: 1 }])
        setInvoicedAt('')
        setShippedAt('')
      }
      setError(null)
      loadProducts()
    }
  }, [open, order])

  // Re-validate rows after products load to ensure SKUs are in the product list
  useEffect(() => {
    if (
      !productsLoading &&
      products.length > 0 &&
      isEditMode &&
      rows.length > 0
    ) {
      // Rows already set from the previous effect, just ensure they're valid
      // This prevents the MUI warning about out-of-range select values
      const validatedRows = rows.map((row) => {
        const product = products.find((p) => p.sku === row.sku)
        if (product && !row.name) {
          return {
            ...row,
            name: product.name,
            unitPrice: product.discountedPrice ?? product.price,
            currency: product.currency,
          }
        }
        return row
      })
      setRows(validatedRows)
    }
  }, [productsLoading, products, isEditMode])

  const loadProducts = async () => {
    try {
      setProductsLoading(true)
      const result = await orderApi.fetchProducts()
      if (Array.isArray(result)) {
        setProducts(result)
      } else {
        setProducts([])
        if (!isEditMode) setError(t('failed_to_load_products'))
      }
    } catch (err: any) {
      const errorMessage = err?.message || t('failed_to_load_products')
      if (!isEditMode) {
        setError(
          typeof errorMessage === 'string'
            ? errorMessage
            : t('failed_to_load_products'),
        )
      }
      setProducts([])
    } finally {
      setProductsLoading(false)
    }
  }

  const handleAddRow = () => {
    setRows([
      ...rows,
      { sku: '', name: '', quantity: 1, unitPrice: 0, currency: 'USD' },
    ])
  }

  const handleRemoveRow = (index: number) => {
    if (rows.length > 1) {
      setRows(rows.filter((_, i) => i !== index))
    }
  }

  const handleRowChange = (
    index: number,
    field: keyof OrderRow,
    value: any,
  ) => {
    const newRows = [...rows]
    newRows[index] = { ...newRows[index], [field]: value }

    if (field === 'sku' && value) {
      const product = products.find((p) => p.sku === value)
      if (product) {
        newRows[index].unitPrice = product.discountedPrice ?? product.price ?? 0
        newRows[index].currency = product.currency || 'USD'
        newRows[index].name = product.name
      }
    }

    setRows(newRows)
  }

  const isValid = () => {
    return rows.every((row) => row.sku && row.quantity > 0)
  }

  const handleSubmit = async () => {
    if (!isValid()) {
      setError(t('please_fill_all_fields'))
      return
    }

    try {
      setLoading(true)
      setError(null)

      if (isEditMode) {
        const enrichedRows = rows.map((row) => ({
          sku: row.sku,
          quantity: row.quantity,
          name: row.name,
          unitPrice: row.unitPrice || 0,
          currency: row.currency || 'USD',
        }))

        const updates: any = { orderDetails: enrichedRows }
        if (isAdmin) {
          if (invoicedAt) updates.invoicedAt = invoicedAt
          if (shippedAt) updates.shippedAt = shippedAt
        }

        await dispatch(
          editOrder({ id: order.id, data: updates }) as any,
        ).unwrap()
        onOrderUpdated?.()
      } else {
        const enrichedRows = rows.map((row) => {
          const product = products.find((p) => p.sku === row.sku)
          return {
            sku: row.sku,
            quantity: row.quantity,
            name: product?.name || row.sku,
            unitPrice: product?.discountedPrice ?? product?.price ?? 0,
            currency: product?.currency || 'USD',
          }
        })
        await dispatch(placeOrder(enrichedRows) as any).unwrap()
        onOrderPlaced?.()
        setRows([{ sku: '', name: '', quantity: 1 }])
      }

      // Blur active element to prevent focus-on-hidden-element warnings
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
      onClose()
    } catch (err: any) {
      const key = isEditMode
        ? 'failed_to_update_order'
        : 'failed_to_place_order'
      const errorMessage = err?.message || err || t(key)
      setError(typeof errorMessage === 'string' ? errorMessage : t(key))
    } finally {
      setLoading(false)
    }
  }

  if (isEditMode && !order) return null

  const CATEGORY_ORDER = ['kits', 'bundles', 'custom']

  const renderProductMenuItems = (isSelectDisabled: boolean) => {
    if (isSelectDisabled) return null

    const grouped: { [key: string]: Product[] } = {}
    products.forEach((product) => {
      const category = product.category || '__ungrouped__'
      if (!grouped[category]) {
        grouped[category] = []
      }
      grouped[category].push(product)
    })

    const sortedEntries = Object.entries(grouped).sort(([catA], [catB]) => {
      const indexA = CATEGORY_ORDER.indexOf(catA)
      const indexB = CATEGORY_ORDER.indexOf(catB)
      const orderA = indexA === -1 ? Infinity : indexA
      const orderB = indexB === -1 ? Infinity : indexB
      return orderA - orderB
    })

    return sortedEntries.flatMap(([category, categoryProducts]) => {
      const items: React.JSX.Element[] = []

      if (category !== '__ungrouped__') {
        items.push(
          <ListSubheader key={`header-${category}`}>
            {t(category)}
          </ListSubheader>,
        )
      }

      categoryProducts.forEach((product) => {
        items.push(
          <MenuItem key={product.sku} value={product.sku}>
            {product.name} ({product.sku})
          </MenuItem>,
        )
      })

      return items
    })
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableRestoreFocus
      data-testid={
        isEditMode ? TEST_IDS.editOrderDialog : TEST_IDS.placeOrderDialog
      }
    >
      <DialogTitle id={TEST_IDS.modalDialogTitle}>
        {isEditMode ? t('edit_order') : t('place_order')}
      </DialogTitle>
      <DialogContent data-testid={TEST_IDS.modalDialogContent}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {typeof error === 'string' ? error : String(error)}
          </Alert>
        )}

        {isLocked && !isAdmin && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('order_cannot_edit')}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('product')}</TableCell>
                  <TableCell align="right">{t('price')}</TableCell>
                  <TableCell align="right">{t('quantity')}</TableCell>
                  <TableCell align="right">{t('total')}</TableCell>
                  <TableCell align="center">{t('action')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isEditMode && productsLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={30} />
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {rows.map((row, index) => {
                      const selectedProduct = products.find(
                        (p) => p.sku === row.sku,
                      )
                      const isSelectDisabled =
                        (isLocked && !isAdmin) ||
                        productsLoading ||
                        products.length === 0
                      return (
                        <TableRow
                          key={index}
                          data-testid={TEST_IDS.orderProductRow}
                        >
                          <TableCell>
                            <Select
                              value={row.sku}
                              onChange={(e) =>
                                handleRowChange(index, 'sku', e.target.value)
                              }
                              fullWidth
                              size="small"
                              disabled={isSelectDisabled}
                              data-testid={TEST_IDS.orderProductSelect}
                            >
                              <MenuItem value="">
                                <em>
                                  {!isEditMode && productsLoading
                                    ? t('loading')
                                    : !isEditMode && products.length === 0
                                      ? t('no_products_available')
                                      : t('select_product')}
                                </em>
                              </MenuItem>
                              {renderProductMenuItems(
                                !isEditMode &&
                                  (productsLoading || products.length === 0),
                              )}
                            </Select>
                          </TableCell>
                          <TableCell align="right">
                            {selectedProduct ? (
                              getProductPriceDisplay(selectedProduct)
                            ) : row.unitPrice ? (
                              <Typography variant="body2">
                                {formatPrice(
                                  row.unitPrice,
                                  row.currency || 'USD',
                                )}
                              </Typography>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              value={row.quantity}
                              onChange={(e) =>
                                handleRowChange(
                                  index,
                                  'quantity',
                                  parseInt(e.target.value, 10) || 0,
                                )
                              }
                              slotProps={{
                                htmlInput: {
                                  min: 1,
                                  style: { textAlign: 'right' },
                                },
                              }}
                              size="small"
                              sx={{ width: '80px' }}
                              disabled={isLocked && !isAdmin}
                              data-testid={TEST_IDS.orderQuantityInput}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {selectedProduct || row.unitPrice ? (
                              <Typography variant="body2">
                                {formatPrice(
                                  (selectedProduct
                                    ? (selectedProduct.discountedPrice ??
                                      selectedProduct.price)
                                    : row.unitPrice || 0) * row.quantity,
                                  selectedProduct?.currency ??
                                    row.currency ??
                                    'USD',
                                )}
                              </Typography>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {rows.length > 1 && (
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveRow(index)}
                                disabled={isLocked && !isAdmin}
                                data-testid={TEST_IDS.removeOrderRowButton}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                    <TableRow
                      sx={{
                        backgroundColor: 'action.hover',
                        fontWeight: 'bold',
                      }}
                    >
                      <TableCell colSpan={3} align="right">
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {t('total')}:
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {formatPrice(
                            rows.reduce((sum, row) => {
                              const product = products.find(
                                (p) => p.sku === row.sku,
                              )
                              const price = product
                                ? (product.discountedPrice ?? product.price)
                                : row.unitPrice || 0
                              return sum + price * row.quantity
                            }, 0),
                            products[0]?.currency ?? rows[0]?.currency ?? 'USD',
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddRow}
              variant="outlined"
              size="small"
              disabled={isLocked && !isAdmin}
              data-testid={TEST_IDS.addOrderRowButton}
            >
              {t('add_product_row')}
            </Button>
          </Box>
        </Box>

        {isEditMode && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label={t('invoiced_at')}
                type="date"
                value={invoicedAt}
                onChange={(e) => setInvoicedAt(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                size="small"
                disabled={!isAdmin}
              />
              <TextField
                label={t('shipped_at')}
                type="date"
                value={shippedAt}
                onChange={(e) => setShippedAt(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                size="small"
                disabled={!isAdmin}
              />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !isValid() || (isLocked && !isAdmin)}
          data-testid={TEST_IDS.submitOrderButton}
        >
          {loading
            ? t(isEditMode ? 'updating_order' : 'placing_order')
            : t(isEditMode ? 'update_order' : 'place_order')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
